// @ts-nocheck
import '@material/web/list/list';
import '@material/web/list/list-item';
import '@material/web/icon/icon.js';
import 'material-symbols/rounded.scss';
import styled from '@emotion/styled';
import { CardContainer } from '../card/card-container';
import { Button } from '../../atoms/button/button';
import { Spacing } from '../../atoms/spacing/spacing';
import {
  Typography,
  TypographyVariant,
} from '../../atoms/typography/typography';
import React, { PropsWithChildren } from 'react';
import {
  PrefixSuffixContent,
  PrefixSuffixContentProps,
} from '../../shared/shared';
import { Divider } from '../../atoms/divider/divider';
import { Badge } from '../../atoms/badge/badge';

const StyledLeadingWrapper = styled.div`
  display: flex;
  align-items: center;
  padding: var(--rk-spacing-xl) 0 var(--rk-spacing-xl) var(--rk-spacing-xl);
`;
export interface ListProps extends PropsWithChildren {
  heading: string | React.ReactNode;
  headingEllipsis?: number;
  supportingText?: string | React.ReactNode;
  trailingSupportingText?: string;
  trailingSupportingTextVariant?: TypographyVariant;
  overlineText?: string | React.ReactNode;
  overlineTextVariant?: TypographyVariant;
  type?: string;
  leadingIcon?: PrefixSuffixContentProps | React.ReactNode;
  trailingIcon?: PrefixSuffixContentProps | React.ReactNode;
  buttonEnabled?: boolean;
  buttonText?: string;
  buttonDataCy?: string;
  alignment?: 'center' | 'top';
  disabled?: boolean;
  trailingComponent?: React.ReactNode;
  trailingComponent?: React.ReactNode;
  trailingContainerDirection?: 'row' | 'column';
  listContainerStyle?:
    | 'filled'
    | 'outline'
    | 'filledWithBorder'
    | 'elevated'
    | 'transparent'
    | 'error';
  divider?: boolean;
  onClick?: () => void;
  onButtonClick?: () => void;
  onTrailingIconClick?: () => void;
  trailingIconDataCy?: string;
}
export const List = ({
  heading,
  headingEllipsis = 2,
  supportingText,
  trailingSupportingText,
  trailingSupportingTextVariant = 'textLabelMediumSurfaceVariant',
  overlineText,
  overlineTextVariant = 'textLabelMediumSurfaceVariant',
  trailingComponent,
  leadingIcon,
  trailingIcon,
  buttonEnabled,
  buttonText = 'Open',
  buttonDataCy = 'list-button',
  alignment = 'center',
  disabled = false,
  divider = false,
  children,
  listContainerStyle = 'outline',
  onClick,
  onButtonClick,
  onTrailingIconClick,
  trailingContainerDirection,
  trailingIconDataCy,
  ...otherProps
}: ListProps) => {
  // in some cases we want the trailing icon to perform the same action as the card click
  // if no trailing icon click handler is provided, we will call the card click handler
  // this ensures we don't have to duplicate the same logic in both handlers when they are the same
  const handleIconClick = (event: React.MouseEvent) => {
    event.stopPropagation();
    if (onTrailingIconClick) {
      onTrailingIconClick();
    } else {
      onClick?.();
    }
  };

  return (
    <StyledListCardWrapper
      disabled={disabled}
      variant={listContainerStyle}
      minHeight="fit-content"
    >
      <md-list onClick={() => onClick?.()} {...otherProps}>
        <StyledListCard isTop={alignment === 'top'}>
          <StyledLeadingWrapper>
            {leadingIcon && (
              <StyledTrailingIconTextContainer
                direction={leadingIcon.direction}
                slot="start"
              >
                <PrefixSuffixContent
                  direction={leadingIcon.direction}
                  source={leadingIcon?.source}
                  type={leadingIcon?.type}
                  name={leadingIcon?.name}
                  isUpperCase={leadingIcon?.isUpperCase}
                  monogramSize={leadingIcon?.monogramSize}
                />
              </StyledTrailingIconTextContainer>
            )}
            <StyledLeadingContainer>
              {overlineText && (
                <div slot="overline">
                  <Typography ellipseLine={2} variant={overlineTextVariant}>
                    {overlineText}
                  </Typography>
                </div>
              )}
              {heading && (
                <div slot="headline">
                  <Typography
                    ellipseLine={headingEllipsis}
                    variant="textBodyLargeOnSurface"
                  >
                    {heading}
                  </Typography>
                </div>
              )}
              {supportingText && (
                <div slot="supporting-text">
                  <Typography
                    ellipseLine={2}
                    variant="textBodyMediumOnSurfaceVariant"
                  >
                    {supportingText}
                  </Typography>
                </div>
              )}
            </StyledLeadingContainer>
          </StyledLeadingWrapper>
          {(trailingSupportingText || trailingIcon) && (
            <StyledTrailingIconTextContainer
              flexDirection={trailingContainerDirection}
              direction={trailingIcon?.direction}
              onClick={handleIconClick}
              {...(trailingIconDataCy ? { 'data-cy': trailingIconDataCy } : {})}
              slot="end"
            >
              {trailingSupportingText && (
                <StyledTrailingContainer>
                  <div slot="trailing-supporting-text">
                    <Typography
                      ellipseLine={2}
                      variant={trailingSupportingTextVariant}
                    >
                      {trailingSupportingText}
                    </Typography>
                  </div>
                </StyledTrailingContainer>
              )}
              {trailingIcon && (
                <PrefixSuffixContent
                  direction={trailingIcon.direction}
                  source={trailingIcon.source}
                  type={trailingIcon.type}
                  badgeSize={trailingIcon.badgeSize}
                  badgeColor={trailingIcon.badgeColor}
                  badgeText={trailingIcon.badgeText}
                  badgeType={trailingIcon.badgeType}
                  name={trailingIcon.name}
                  isUpperCase={trailingIcon.isUpperCase}
                  monogramSize={trailingIcon.monogramSize}
                  onClick={trailingIcon.onClick}
                  cursor={trailingIcon.cursor}
                  color={trailingIcon.color}
                />
              )}
            </StyledTrailingIconTextContainer>
          )}
          {trailingComponent && (
            <StyledTrailingIconTextContainer>
              {trailingComponent}
            </StyledTrailingIconTextContainer>
          )}
        </StyledListCard>
      </md-list>
      {buttonEnabled && (
        <Spacing marginLeft="xl" marginRight="xl" marginBottom="xl">
          <Button
            data-cy={buttonDataCy}
            onClick={() => {
              onButtonClick?.();
            }}
            width="100%"
            slot="trailing"
            children={buttonText}
          />
        </Spacing>
      )}
      {divider && (
        <Spacing
          marginTop="lg"
          marginBottom="lg"
          marginLeft="xl"
          marginRight="xl"
        >
          <Divider />
        </Spacing>
      )}

      {children}
    </StyledListCardWrapper>
  );
};
interface StyledListProps {
  isTop: boolean;
}
const StyledListCard = styled.div<StyledListProps>`
  display: flex;
  justify-content: space-between;
  flex-direction: row;
  gap: var(--rk-spacing-xl) var(--rk-spacing-xl) var(--rk-spacing-xl) 0;
  align-items: ${(props) => (!props.isTop ? 'center' : 'flex-start')};
`;
const StyledLeadingContainer = styled.div`
  display: flex;
  flex-direction: column;
`;
const StyledTrailingContainer = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 80px;
`;
interface PrefixPaddingProps {
  direction?: 'leading' | 'trailing';
  flexDirection?: 'row' | 'column';
  variant?:
    | 'filled'
    | 'outline'
    | 'filledWithBorder'
    | 'elevated'
    | 'transparent'
    | 'error';
}

const StyledTrailingIconTextContainer = styled.div<PrefixPaddingProps>`
  display: flex;
  align-items: center;
  flex-direction: ${(props) => props.flexDirection ?? 'row'};
  gap: var(--rk-spacing-md);
  padding: ${(props) =>
    props.direction === 'leading'
      ? '0 var(--rk-spacing-xl) 0 0'
      : '0 var(--rk-spacing-xl) 0 var(--rk-spacing-xl)'};
`;
const StyledListCardWrapper = styled(CardContainer)`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  cursor: pointer;
  md-list {
    background: ${(props) => props.variant === 'transparent' && 'transparent'};
  }
`;
